/**
 * BottomTabBar — employee mobile nav (mockup screen 10). 5 tabs with
 * the most-used action (check-in) raised + gradient in the centre.
 * Rendered by PageLayout + Home; admin pages use AdminLayout instead.
 * Pages reserve space via `pb-20`.
 */
import { Link, useLocation } from 'react-router-dom';
import { Home, Inbox, Camera, Wallet, User, type LucideIcon } from 'lucide-react';
import { useMyRequests } from '../../hooks';

function Tab({
  to,
  label,
  icon: Icon,
  active,
  badge,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  badge?: number;
}) {
  return (
    <Link to={to} className={`relative flex flex-col items-center gap-1 ${active ? 'text-teal' : 'text-gray-400'}`}>
      <div className="relative">
        <Icon className="w-5 h-5" />
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className={`text-[11px] ${active ? 'font-medium' : ''}`}>{label}</span>
    </Link>
  );
}

export function BottomTabBar() {
  const { pathname } = useLocation();
  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  // Badge สำหรับ tab "คำขอ" — รวมทั้งใบที่ยัง pending + ใบที่ต้องตอบกลับ
  // (info_requested / conditionally_approved)
  const myReq = useMyRequests({});
  const badge = myReq.data ? myReq.data.summary.pending_count + myReq.data.summary.needs_action : 0;

  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-gray-200 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
      <div className="max-w-2xl mx-auto grid grid-cols-5 items-end pt-2">
        <Tab to="/home" label="หน้าหลัก" icon={Home} active={isActive('/home')} />
        <Tab to="/me/requests" label="คำขอ" icon={Inbox} active={isActive('/me/requests')} badge={badge} />
        {/* Centre — check-in raised + gradient */}
        <div className="flex flex-col items-center">
          <Link
            to="/checkin"
            className="w-14 h-14 -mt-7 rounded-full bg-brand-gradient text-white flex items-center justify-center shadow-lg border-4 border-white"
          >
            <Camera className="w-6 h-6" />
          </Link>
          <span className={`text-[11px] mt-0.5 ${isActive('/checkin') ? 'text-teal font-medium' : 'text-gray-500'}`}>เช็คอิน</span>
        </div>
        <Tab to="/slip" label="สลิป" icon={Wallet} active={isActive('/slip')} />
        <Tab to="/profile" label="ฉัน" icon={User} active={isActive('/profile')} />
      </div>
    </nav>
  );
}
